<template>
	<div ref="swiperBlock" v-editable="blok" class="grid grid-cols-1">
		<div class="swiper-carousel-wrapper">
			<div class="relative">
				<div v-show="showPreview">
					<StoryblokComponent v-if="items?.[0]" :blok="items[0]" is-swiper />
				</div>
				<swiper-container
					v-show="!showPreview"
					ref="containerRef"
					:init="false"
					:autoplay="{
						delay: Number(blok.autoplay || 3000),
						disableOnInteraction: false
					}"
				>
					<swiper-slide v-for="(item, idx) in items" :key="idx">
						<BlokPriorityScope
							:priority="idx === 0 ? firstSlidePriority : false"
						>
							<StoryblokComponent :key="item._uid" :blok="item" is-swiper />
						</BlokPriorityScope>
					</swiper-slide>
				</swiper-container>

				<button
					v-show="blok.arrows"
					class="swiper-custom-button-prev absolute top-1/2 -translate-y-1/2 left-0 z-10 h-12 w-[30px] rounded-full flex items-center justify-center bg-white opacity-50 rounded-l-4xl transition-opacity hover:opacity-100"
					@click="prevSlide"
				>
					<SwiperArrowIcon class="rotate-180" />
				</button>
				<button
					v-show="blok.arrows"
					class="swiper-custom-button-next absolute top-1/2 -translate-y-1/2 right-0 z-10 h-12 w-[30px] rounded-full flex items-center justify-center bg-white opacity-50 rounded-r-4xl transition-opacity hover:opacity-100"
					@click="nextSlide"
				>
					<SwiperArrowIcon />
				</button>
				<div
					v-show="blok.dots"
					class="swiper-custom-pagination absolute z-10 flex justify-center gap-1"
				/>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import type { SwiperCarousel } from "~/types/storyblok/storyblok";
import type { ImagePriority } from "~/layers/base/utils/imagePriority";
import { isDisplayBlock } from "~/utils/displayBlock";

const containerRef = ref<HTMLElement | null>(null);
const swiperBlock = ref<HTMLElement | null>(null);
let observer: IntersectionObserver | null = null;
const isSwiperInitialized = ref(false);

interface Props {
	blok: SwiperCarousel;
}

const { blok } = defineProps<Props>();

const items = computed(() =>
	blok.items?.length
		? blok.items.filter((item) =>
				isDisplayBlock(item.start_date, item.end_date)
			)
		: []
);

const showPreview = ref(true);

// Only the preview inherits a first-screen preload. The first slide repeats the
// preview's image, so it loads eagerly (already fetched, no second preload) and
// does not blank when Swiper swaps in; every other slide stays lazy.
const carouselPriority = useBlokPriority();
const firstSlidePriority = computed<ImagePriority>(() =>
	carouselPriority.value === "high" ? "eager" : "lazy"
);

const initSwiper = async () => {
	if (!containerRef.value || isSwiperInitialized.value) {
		return;
	}

	const { Navigation, Pagination, Autoplay } = await import("swiper/modules");
	const containerEl = containerRef.value as HTMLElement & {
		modules?: unknown[];
		initialize?: () => void;
		swiper?: { slidePrev: () => void; slideNext: () => void };
		loop?: boolean;
		slidesPerView?: number;
		lazyPreloadPrevNext?: number;
		autoplay?: Record<string, unknown>;
		navigation?: Record<string, unknown>;
		pagination?: Record<string, unknown>;
	};

	containerEl.modules = [Navigation, Pagination, Autoplay];
	containerEl.loop = true;
	containerEl.slidesPerView = Number(blok.items_per_view || 1);
	// Slide images are lazy; un-lazy the neighbours so autoplay never lands on a
	// slide that only starts loading as it slides in
	containerEl.lazyPreloadPrevNext = 1;
	containerEl.autoplay = {
		delay: Number(blok.autoplay || 3000),
		disableOnInteraction: false
	};

	if (blok.arrows) {
		containerEl.navigation = {
			prevEl: ".swiper-custom-button-prev",
			nextEl: ".swiper-custom-button-next"
		};
	}

	if (blok.dots) {
		containerEl.pagination = {
			el: ".swiper-custom-pagination",
			clickable: true,
			type: "bullets",
			bulletClass:
				"h-6 w-6 flex items-center !bg-transparent justify-center md:before:h-4 before:h-3 md:before:w-4 before:w-3 opacity-25 before:rounded-full before:bg-white",
			bulletActiveClass: "swiper-pagination-bullet-active opacity-100"
		};
	}

	containerEl.initialize?.();
	isSwiperInitialized.value = true;
	showPreview.value = false;
};

const prevSlide = () => {
	const containerEl = containerRef.value as HTMLElement & {
		swiper?: { slidePrev: () => void };
	};
	containerEl?.swiper?.slidePrev();
};

const nextSlide = () => {
	const containerEl = containerRef.value as HTMLElement & {
		swiper?: { slideNext: () => void };
	};
	containerEl?.swiper?.slideNext();
};

onMounted(() => {
	if (!swiperBlock.value) {
		return;
	}

	observer = new IntersectionObserver(
		(entries) => {
			if (entries.some((entry) => entry.isIntersecting)) {
				void initSwiper();
				observer?.disconnect();
				observer = null;
			}
		},
		{ rootMargin: "200px 0px" }
	);

	observer.observe(swiperBlock.value);
});

onBeforeUnmount(() => {
	observer?.disconnect();
	observer = null;
});
</script>

<style scoped>
.swiper-carousel-wrapper {
	--swiper-pagination-color: white;
	--swiper-pagination-bottom: 12px;
}
</style>
